import React from 'react';

const PsychologicalMatrix = () => {
  return (
    <section id="the-matrix" className="min-h-screen flex items-center py-20 px-4 bg-gradient-to-b from-darker via-dark to-darker">
      <div className="container mx-auto">
        <div className="max-w-6xl mx-auto">
          {/* Section Title */}
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-6xl font-bold text-white mb-6 font-heading">
              Understanding the <span className="bg-gradient-to-r from-danger to-warning bg-clip-text text-transparent">Matrix</span>
            </h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              This isn't about technology or machines — it's about <span className="text-warning font-semibold">mental programming</span>
            </p>
          </div>

          {/* Historical Context */}
          <div className="mb-16 bg-gradient-to-br from-dark/60 to-darker/60 backdrop-blur-sm border-2 border-danger/30 rounded-3xl p-10 md:p-12">
            <div className="flex items-start gap-6 mb-8">
              <div className="text-5xl">⛓️</div>
              <div>
                <h3 className="text-3xl font-bold text-danger mb-4 font-heading">Years of Slavery Built This Mindset</h3>
                <div className="space-y-4 text-lg text-gray-300 leading-relaxed">
                  <p>
                    For <span className="text-warning font-semibold">centuries</span>, our ancestors lived under kings, rulers, and colonizers.
                  </p>
                  <p>
                    They were taught to <span className="text-danger font-semibold">obey — not to question</span>.
                  </p>
                  <p className="text-xl text-white font-semibold">
                    That fear and obedience passed from one generation to the next, until it became our nature.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* The Slave Mentality - NEW SECTION */}
          <div className="mb-16 bg-gradient-to-br from-danger/10 to-transparent border-2 border-danger/40 rounded-3xl p-10 md:p-12">
            <div className="flex items-start gap-6">
              <div className="text-5xl">👔</div>
              <div>
                <h3 className="text-3xl font-bold text-danger mb-6 font-heading">The Slave Becomes The Master</h3>
                <div className="space-y-4 text-lg text-gray-300 leading-relaxed">
                  <p className="text-xl text-white font-semibold">
                    Here's the twisted part of this matrix:
                  </p>
                  <p>
                    When someone who grew up in this system finally gets some <span className="text-warning font-semibold">rank or authority</span> — whether as a boss, professor, government officer, or manager —
                  </p>
                  <p className="text-xl text-danger font-bold">
                    They treat the people under them exactly like slaves.
                  </p>
                  <p>
                    Because that's what they learned. That's what they experienced. <span className="text-white font-semibold">Power means control</span>, not responsibility.
                  </p>
                  <div className="mt-6 bg-danger/10 border-l-4 border-danger pl-6 py-4 rounded-r-lg">
                    <p className="text-white font-semibold text-lg">
                      These people become the <span className="text-danger">biggest barrier for growth</span> — crushing new ideas, blocking innovation, and maintaining the same old hierarchy.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Where We See It */}
          <div className="mb-16">
            <h3 className="text-3xl font-bold text-white mb-8 text-center font-heading">
              Where Do We See This Slave Mentality?
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-gradient-to-br from-warning/10 to-transparent border-2 border-warning/30 rounded-2xl p-6">
                <div className="text-4xl mb-3">🏢</div>
                <h5 className="text-xl font-bold text-warning mb-3">In Workplaces</h5>
                <p className="text-gray-300">
                  Bosses who micro-manage everything, don't trust employees, treat them like servants instead of team members
                </p>
              </div>

              <div className="bg-gradient-to-br from-danger/10 to-transparent border-2 border-danger/30 rounded-2xl p-6">
                <div className="text-4xl mb-3">🎓</div>
                <h5 className="text-xl font-bold text-danger mb-3">In Universities</h5>
                <p className="text-gray-300">
                  Teachers who don't allow questions, who punish students for thinking differently, who want blind obedience
                </p>
              </div>

              <div className="bg-gradient-to-br from-secondary/10 to-transparent border-2 border-secondary/30 rounded-2xl p-6">
                <div className="text-4xl mb-3">🏛️</div>
                <h5 className="text-xl font-bold text-secondary mb-3">In Government Offices</h5>
                <p className="text-gray-300">
                  Officers who make people wait unnecessarily, who demand respect without earning it, who abuse their authority
                </p>
              </div>

              <div className="bg-gradient-to-br from-warning/10 to-transparent border-2 border-warning/30 rounded-2xl p-6">
                <div className="text-4xl mb-3">👨‍👩‍👧‍👦</div>
                <h5 className="text-xl font-bold text-warning mb-3">Even At Home</h5>
                <p className="text-gray-300">
                  Parents (not all, but many) who don't see children as individuals but as property to control
                </p>
              </div>
            </div>
          </div>

          {/* The Path */}
          <div className="text-center mb-16">
            <div className="inline-block bg-gradient-to-r from-danger/20 via-warning/20 to-danger/20 border-2 border-danger/40 rounded-full px-12 py-6">
              <div className="flex flex-wrap items-center justify-center gap-4 text-2xl md:text-3xl font-bold">
                <span className="text-gray-400">Study</span>
                <span className="text-danger">→</span>
                <span className="text-gray-400">Job</span>
                <span className="text-danger">→</span>
                <span className="text-gray-400">Marriage</span>
                <span className="text-danger">→</span>
                <span className="text-gray-400">Survive</span>
                <span className="text-danger">→</span>
                <span className="text-danger">Die</span>
              </div>
            </div>
            <p className="text-gray-500 mt-6 text-lg italic">The predetermined path most still follow</p>
          </div>

          {/* It's Not Just Parents */}
          <div className="mb-16 bg-gradient-to-br from-primary/10 to-accent/10 border-2 border-primary/30 rounded-3xl p-10 md:p-12">
            <div className="text-center mb-6">
              <div className="text-5xl mb-4">🤝</div>
              <h3 className="text-3xl font-bold text-white mb-6 font-heading">
                It's Not Just About Parents
              </h3>
            </div>
            <div className="space-y-4 text-lg text-gray-300 leading-relaxed max-w-4xl mx-auto">
              <p className="text-xl text-white">
                This is a <span className="text-primary font-semibold">system-wide problem</span>:
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
                <div className="flex items-start gap-3">
                  <span className="text-primary text-2xl">•</span>
                  <span><span className="text-white font-semibold">Parents</span> who learned from their parents</span>
                </div>
                <div className="flex items-start gap-3">
                  <span className="text-accent text-2xl">•</span>
                  <span><span className="text-white font-semibold">Teachers</span> who enforce outdated systems</span>
                </div>
                <div className="flex items-start gap-3">
                  <span className="text-secondary text-2xl">•</span>
                  <span><span className="text-white font-semibold">Bosses</span> who replicate toxic hierarchies</span>
                </div>
                <div className="flex items-start gap-3">
                  <span className="text-warning text-2xl">•</span>
                  <span><span className="text-white font-semibold">Society</span> that judges every choice</span>
                </div>
              </div>
              <p className="text-xl text-accent font-semibold text-center mt-8 pt-6 border-t-2 border-primary/30">
                Everyone is trapped in the same cycle — victims and perpetuators at the same time.
              </p>
            </div>
          </div>

          {/* Hope Message */}
          <div className="text-center">
            <div className="inline-block bg-gradient-to-r from-accent/20 to-primary/20 border-2 border-accent/40 rounded-2xl px-10 py-8 max-w-3xl">
              <p className="text-2xl md:text-3xl text-white font-semibold mb-4">
                But not everyone is trapped...
              </p>
              <p className="text-xl text-gray-300 leading-relaxed">
                Many are waking up — especially our generation, <span className="text-accent font-bold">Gen Z</span>.<br />
                We question things more. We explore. We express. We refuse to repeat the cycle.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PsychologicalMatrix;